import { useEffect, useRef, useState, type FormEvent } from 'react';
import type { DeletedResponse, FellowConnectionResponse, JobAcceptedResponse, PutFellowConnectionRequest } from '../../shared/api-types.js';
import { DEFAULT_FELLOW_WORKSPACE, autoProcessWindowLabel } from '../../shared/fellow.js';
import { api, type ApiFailure } from '../api.js';
import {
  Banner,
  Button,
  ConfirmDialog,
  EM_DASH,
  FailureView,
  FormErrorSummary,
  Icon,
  LinkButton,
  RelativeTime,
  Section,
  StateView,
  StatusPill,
  TextField,
  failureDetail,
  inlineFailureText,
  useRequest,
  useToast,
} from '../ui/index.js';
import {
  FELLOW_REVOKED,
  TICK_OFF_REVIEW_PATH,
  connectionView,
  dailyLimitText,
  fellowConnectedDescription,
  fellowCredentialRevoked,
  fellowPanelDescription,
  fellowStatePresentation,
  fellowStateRowPresentation,
  maskedKey,
  tickOffFailingTitle,
  tickOffPresentation,
} from './lib/connection.js';
import { UnattendedStreakRow } from './connection-run-rows.js';
import { NEVER, connectionRunsPath, runTriggerPresentation } from './lib/connection-runs.js';

// The Fellow connection (spec §13): four distinct screens — not configured (the deployment has
// Fellow switched off), not permitted (no fellow_connect grant), not connected (the form), and
// connected (details, sync, disconnect). The API key is written once and never read back; the page
// only ever sees an eight-character fingerprint of it, never any part of it (06-modules §6.4 Credential;
// conformity finding D7). Every state renders inside the panel's own titled section, with its actions
// inside it too (03-components §7), so the Connections page can hold other panels. The connected facts
// answer 06-modules §6's questions (findings D5, S5): the State of the most recent run, the last success
// with "— never" where there has been none, how the last run began, the unattended streak, and the way to
// the recent runs (§6.3 R2).

const SYNC_POLL_MS = 4_000;
const SYNC_POLL_MAX = 45;

export interface FellowPanelProps {
  /**
   * Told whether this panel offers a "Connect …" action (the form, or connecting again after a
   * failure), and null until it knows. The page keeps one primary action: the first such offer in
   * document order (03-components §2).
   */
  onConnectOffered?: (offered: boolean | null) => void;
}

export function FellowPanel({ onConnectOffered }: FellowPanelProps) {
  const toast = useToast();
  const request = useRequest<FellowConnectionResponse>((signal) => api.get<FellowConnectionResponse>('/api/connections/fellow', { signal }), []);
  const data = request.data;
  const [syncing, setSyncing] = useState(false);
  const [syncingAll, setSyncingAll] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [disconnectError, setDisconnectError] = useState<string | null>(null);
  const [polls, setPolls] = useState(0);
  // Why: a failing connection is repaired by supplying a new key (PUT replaces it in place), so
  // the form must be reachable from the connected screen, not only from the empty one.
  const [reconnecting, setReconnecting] = useState(false);

  const view = data ? connectionView(data) : null;
  const offersConnect = data === null ? null : view === 'not-connected' || (view === 'connected' && data.allowed && data.connection?.status === 'error');
  useEffect(() => {
    onConnectOffered?.(offersConnect);
  }, [offersConnect, onConnectOffered]);

  // While a sync runs, refresh so "last synced" and any error land without a manual reload.
  const inProgress = data?.connection?.syncInProgress === true;
  useEffect(() => {
    if (!inProgress || polls >= SYNC_POLL_MAX) return;
    const handle = window.setTimeout(() => {
      setPolls((count) => count + 1);
      request.reload();
    }, SYNC_POLL_MS);
    return () => window.clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inProgress, polls, data?.connection?.lastSyncStartedAt]);

  const syncNow = async (): Promise<void> => {
    setSyncing(true);
    const result = await api.post<JobAcceptedResponse>('/api/connections/fellow/sync');
    setSyncing(false);
    if (!result.ok) {
      toast.error(<>We couldn&rsquo;t start a sync. {failureDetail(result)}</>);
      return;
    }
    toast.success(result.data.alreadyQueued ? 'A sync is already running; new meetings will appear as it finishes.' : 'Sync started — new meetings will appear as it finishes.');
    setPolls(0);
    request.reload();
  };

  // Why a second sync (2026-09-28): Sync now asks Fellow only for what changed since the last sync, so a
  // recording that became visible to the person later (shared with them, or recorded by a colleague's
  // Fellow) is never fetched; this one asks for everything the key can see. Nothing is duplicated.
  const syncEverything = async (): Promise<void> => {
    setSyncingAll(true);
    const result = await api.post<JobAcceptedResponse>('/api/connections/fellow/sync-all');
    setSyncingAll(false);
    if (!result.ok) {
      toast.error(<>We couldn&rsquo;t start a full sync. {failureDetail(result)}</>);
      return;
    }
    toast.success(result.data.alreadyQueued ? 'A full sync is already on its way; missing meetings will appear as it finishes.' : 'Full sync started — missing meetings will appear as it finishes.');
    setPolls(0);
    request.reload();
  };

  const disconnect = async (): Promise<void> => {
    setDisconnecting(true);
    setDisconnectError(null);
    const result = await api.delete<DeletedResponse>('/api/connections/fellow');
    setDisconnecting(false);
    if (!result.ok) {
      setDisconnectError(result.correlationId ? `${inlineFailureText(result)} (reference ${result.correlationId})` : inlineFailureText(result));
      return;
    }
    setConfirmDisconnect(false);
    toast.success('Fellow disconnected. Your imported meetings and to-dos are kept.');
    request.reload();
  };

  // Why the tick-off and Stage 3 sentences only where this server and the account let them (owner, 2026-09-30; O4(a)):
  // the description never promises what the switch withholds. The connect form's state adds the processing window
  // (spec §13: its words derived from autoProcessWindowLabel()); "stores it encrypted" is the API key hint's job.
  const panelDescription = fellowPanelDescription(data?.tickOff ?? { floor: 'off' });
  const description = view === 'connected' && data
    ? fellowConnectedDescription(data)
    : view === 'not-connected'
      ? `${panelDescription} Meetings from the last ${autoProcessWindowLabel()} are processed automatically; older ones are imported and wait for you to process them.`
      : panelDescription;

  return (
    <Section id="fellow-connection" title="Fellow" description={description}>
      {request.status === 'loading' && !data ? <StateView kind="loading" label="Checking your Fellow connection" minHeight="16rem" /> : null}
      {request.status === 'error' && request.error && !data ? (
        <FailureView failure={request.error} what="your Fellow connection" onRetry={request.reload} featureName="The Fellow integration" minHeight="16rem" />
      ) : null}

      {data ? (() => {
        switch (view) {
          case 'not-configured':
            return (
              <StateView
                kind="not-configured"
                title="Fellow is not switched on in this installation"
                description={
                  <>
                    <p>Nothing here is failing — an operator can switch it on.</p>
                    {data.connection ? <p className="text-sm muted">Your earlier connection to {data.connection.workspaceName ?? data.connection.workspaceSubdomain} is kept but inactive until then.</p> : null}
                  </>
                }
                minHeight="16rem"
              />
            );
          case 'not-permitted':
            return (
              <StateView
                kind="denied"
                title="Your account cannot connect Fellow"
                description="Your access doesn’t include Connect Fellow. Ask an administrator to add you to a group that has it."
                minHeight="16rem"
              />
            );
          case 'not-connected':
            return <ConnectForm onConnected={(response) => { request.setData(response); toast.success(`Connected to ${response.connection?.workspaceName ?? 'Fellow'} — importing your meetings now.`); setPolls(0); }} previous={data.connection} />;
          case 'connected':
          default:
            return data.connection ? (
              <>
                <ConnectedView
                  data={data}
                  syncing={syncing}
                  onSync={() => void syncNow()}
                  syncingAll={syncingAll}
                  onSyncAll={() => void syncEverything()}
                  onDisconnect={() => { setDisconnectError(null); setConfirmDisconnect(true); }}
                  onReconnect={data.allowed && !reconnecting ? () => setReconnecting(true) : undefined}
                  refreshing={request.refreshing}
                />
                {reconnecting && data.allowed ? (
                  <ConnectForm
                    title="Connect again"
                    previous={data.connection}
                    onConnected={(response) => { request.setData(response); setReconnecting(false); toast.success(`Reconnected to ${response.connection?.workspaceName ?? 'Fellow'} — importing your meetings now.`); setPolls(0); }}
                    onCancel={() => setReconnecting(false)}
                  />
                ) : null}
              </>
            ) : null;
        }
      })() : null}

      <ConfirmDialog
        open={confirmDisconnect}
        title={`Disconnect ${data?.connection?.workspaceName ?? data?.connection?.workspaceSubdomain ?? 'Fellow'}?`}
        confirmLabel="Disconnect"
        danger
        busy={disconnecting}
        error={disconnectError}
        onCancel={() => { if (!disconnecting) setConfirmDisconnect(false); }}
        onConfirm={disconnect}
      >
        <p>Calis will delete the stored API key and stop importing meetings. Your imported meetings, summaries and to-dos are kept. You can connect again at any time with a new key.</p>
      </ConfirmDialog>
    </Section>
  );
}

// --- Connected ---------------------------------------------------------------------------------

/** Exported for the rendering test (test/server/client-pages/fellow-revoked.test.ts); the panel composes it. */
export function ConnectedView({ data, syncing, onSync, syncingAll, onSyncAll, onDisconnect, onReconnect, refreshing }: {
  data: FellowConnectionResponse;
  syncing: boolean;
  onSync: () => void;
  syncingAll: boolean;
  onSyncAll: () => void;
  onDisconnect: () => void;
  /** Offered while the connection is failing and the reader may connect. */
  onReconnect?: () => void;
  refreshing: boolean;
}) {
  const connection = data.connection;
  if (!connection) return null;
  // Why the most recent run's state and not the connection's (06-modules §6.4 State; finding S5): "Active" said the
  // connection stood, not whether its last sync completed — the question the reader has. The connection's own
  // condition still shows: a failing one in the banner above and in "Connect again" below.
  // A run Fellow refused the key on reads Revoked (the owner's ruling of 2026-10-09, "Both say Revoked"; lib/connection.ts).
  const lastRun = fellowStateRowPresentation(data.runs, connection.syncInProgress);
  const lastTrigger = data.runs.last ? runTriggerPresentation(data.runs.last.trigger) : null;
  const workspace = connection.workspaceName ?? connection.workspaceSubdomain;
  const tickOff = tickOffPresentation(data);
  const failingTitle = tickOffFailingTitle(data.tickOff.failing);
  const fellowState = fellowStatePresentation(data);
  // Today's limit pauses both directions, which share its 500 requests: said once, for whichever the reader has.
  const dailyLimit = dailyLimitText({ tickOff: tickOff.kind === 'daily-limit', fellowState: fellowState.kind === 'daily-limit' });
  // Why a key Fellow refused reads Revoked, and as a warning (05-content §6; 06-modules §6.4 Credential; the Phase 1c
  // audit's R12): Fellow itself withdrew its acceptance, and the reader's next act is to connect again — which "Connect
  // again" below offers — never to retry the sync, which "The last sync failed" invited.
  const revoked = fellowCredentialRevoked(connection);
  const errorTitle = revoked ? FELLOW_REVOKED.bannerTitle : connection.status === 'error' ? 'The last sync failed' : 'The last sync had a problem';
  return (
    <div className="stack stack--3">
      {connection.lastError ? (
        <Banner tone={connection.status === 'error' && !revoked ? 'danger' : 'warning'} title={errorTitle}>
          <p>{connection.lastError.message}</p>
          <p className="text-xs muted">
            {connection.lastError.at ? <>Recorded <RelativeTime value={connection.lastError.at} mode="both" />. </> : null}
            Code <code className="selectable">{connection.lastError.code}</code>
          </p>
        </Banner>
      ) : null}
      {!data.allowed ? (
        <Banner tone="warning" title="Syncing is paused for your account">
          Your account no longer includes the Connect Fellow capability, so Calis is not importing new meetings. Ask an administrator if you need it restored; you can still disconnect.
        </Banner>
      ) : null}
      {/* Ticking off in Fellow (owner, 2026-09-30): what did not get through, with the way to it — a banner, since the
          condition persists until the person acts (03-components §6.2) — and today's limit, which passes by itself. */}
      {failingTitle ? (
        <Banner
          tone="warning"
          title={failingTitle}
          actions={<LinkButton to={TICK_OFF_REVIEW_PATH} variant="secondary" size="sm">Review<span className="visually-hidden"> the to-dos not ticked off in Fellow</span></LinkButton>}
        >
          Open each one to try again; it says why it didn&rsquo;t get through.
        </Banner>
      ) : null}
      {dailyLimit !== null ? (
        <Banner tone="info" title="Today’s limit of Fellow requests is reached">
          {dailyLimit}
        </Banner>
      ) : null}
      {/* Fellow → Calis (Stage 3): the last read of the action-item list did not get through. A warning banner, since the
          condition persists until a read is clean (03-components §6.2): what failed, then what to do meanwhile. */}
      {fellowState.banner ? (
        <Banner tone="warning" title={fellowState.banner.title} className="fellow-state-banner">
          <p>{fellowState.banner.text}</p>
          <p>{fellowState.banner.remedy}</p>
        </Banner>
      ) : null}

      <dl className="field-list sheet">
        <dt>State</dt>
        <dd className="connection-run-state" data-state={lastRun?.revoked ? 'revoked' : (lastRun?.run?.state ?? 'none')}>
          {lastRun ? <StatusPill tone={lastRun.tone} icon={lastRun.icon} title={lastRun.detail}>{lastRun.word}</StatusPill> : <span className="muted">{NEVER}</span>}
          {/* The time is the time of the run the word describes — never another's: Pending names a run that has not begun.
              Revoked says the next act first, then the run that met the refusal, as the Credential row says it below. */}
          {lastRun?.revoked && lastRun.run ? (
            <>
              <span className="text-sm"> {FELLOW_REVOKED.nextStep}</span>
              <span className="text-sm muted"> {FELLOW_REVOKED.stateRunNote} <RelativeTime value={lastRun.run.startedAt} />.</span>
            </>
          ) : lastRun?.run && lastRun.run.state !== 'running' ? <span className="text-sm muted"> of the sync <RelativeTime value={lastRun.run.startedAt} /></span> : null}
          {refreshing ? <span className="visually-hidden"> (refreshing)</span> : null}
        </dd>
        <dt>Workspace</dt>
        <dd>{workspace} <span className="muted">({connection.workspaceSubdomain}.fellow.app)</span></dd>
        <dt>Fellow account</dt>
        <dd>{connection.fellowUserName ?? EM_DASH}{connection.fellowUserEmail ? <span className="muted"> · {connection.fellowUserEmail}</span> : null}</dd>
        <dt>Credential</dt>
        <dd className="fellow-credential" data-state={revoked ? 'revoked' : 'accepted'}>
          {/* "A credential the source no longer accepts is Revoked" is this row's own clause (06-modules §6.4 Credential), so
              the word stands here; the State row above says it too of the run Fellow refused the key on (the owner's ruling of
              2026-10-09, "Both say Revoked"). */}
          {revoked ? (
            <span className="fellow-credential__state">
              <StatusPill tone="warning" icon="unplug" title={FELLOW_REVOKED.detail}>{FELLOW_REVOKED.word}</StatusPill>
              <span className="text-sm"> {FELLOW_REVOKED.nextStep}</span>{' '}
            </span>
          ) : null}
          {/* A fingerprint, as the Slack panel shows its token's (06-modules §6.4 Credential): never the key, nor any part
              of it. A connection from before migration 0048 has none until its next sync records it. */}
          {connection.credentialFingerprint ? <code className="selectable">{maskedKey(connection.credentialFingerprint)}</code> : <span className="muted">{EM_DASH}</span>}{' '}
          <span className="muted text-xs">{connection.credentialFingerprint ? '(a fingerprint, not the key; the key is stored encrypted)' : '(the key is stored encrypted; its fingerprint appears after the next sync)'}</span>
        </dd>
        <dt>Last success</dt>
        <dd>
          {/* The last sync that COMPLETED, never merely the last run; "— never" where there has been none (§6.4, §6.7). */}
          {connection.lastSyncCompletedAt ? <RelativeTime value={connection.lastSyncCompletedAt} mode="both" /> : <span className="muted">{NEVER}</span>}
        </dd>
        <dt>Trigger</dt>
        <dd className="connection-run-trigger" data-trigger={data.runs.last?.trigger ?? 'none'}>
          {/* How the most recent run began (§6.4 Trigger) — worded as how it began, never with a state word. */}
          {lastTrigger && data.runs.last ? (
            <>
              <Icon name={lastTrigger.icon} size={14} /> {lastTrigger.word}
              <span className="text-sm muted"> · started <RelativeTime value={data.runs.last.startedAt} mode="both" /></span>
            </>
          ) : <span className="muted">{NEVER}</span>}
        </dd>
        <UnattendedStreakRow summary={data.runs} system="fellow" />
        <dt>Connected</dt>
        <dd><RelativeTime value={connection.createdAt} mode="both" /></dd>
        <dt>Ticking off in Fellow</dt>
        <dd className="fellow-tick-off" data-state={tickOff.kind}>
          {tickOff.word}
          <span className="text-sm muted fellow-tick-off__detail">{tickOff.detail}</span>
        </dd>
        <dt>Changes made in Fellow</dt>
        <dd className="fellow-state" data-state={fellowState.kind}>
          {fellowState.word}
          <span className="text-sm muted fellow-state__detail">{fellowState.detail}</span>
        </dd>
      </dl>

      {/* Why secondary: a routine action on a connected panel; the page's one primary is the first
          "Connect …" offer, if any (03-components §2). Connecting again after a failure is one. */}
      <div className="connection-actions">
        {data.allowed ? (
          <Button variant="secondary" icon="refresh" onClick={onSync} loading={syncing} disabled={connection.syncInProgress} aria-describedby={connection.syncInProgress ? 'sync-running-note' : undefined}>
            {connection.syncInProgress ? 'Sync running' : 'Sync now'}
          </Button>
        ) : null}
        {/* Why ghost: a rarer act beside Sync now, never a second secondary competing with it. */}
        {data.allowed ? (
          <Button variant="ghost" onClick={onSyncAll} loading={syncingAll} disabled={connection.syncInProgress} aria-describedby="sync-everything-note">
            Sync everything again
          </Button>
        ) : null}
        {onReconnect && connection.status === 'error' ? (
          <Button variant="primary" icon="key" onClick={onReconnect}>Connect again</Button>
        ) : null}
        {/* The route from the system to its run history (06-modules §6.3 R2, §6.5): a real link, named with its system
            since the Slack panel on this page offers the same. */}
        <LinkButton to={connectionRunsPath('fellow')} variant="ghost" icon="list">Recent Fellow syncs</LinkButton>
        {/* Named with its service: the Slack panel's "Disconnect Slack" is on the same page (05-content §2). */}
        <Button variant="danger-outline" icon="unplug" onClick={onDisconnect} className="connection-actions__danger">Disconnect Fellow</Button>
      </div>
      {connection.syncInProgress ? <p id="sync-running-note" className="text-sm muted">A sync is running; this page refreshes by itself.</p> : null}
      {data.allowed ? <p id="sync-everything-note" className="text-sm muted">If a meeting you can see in Fellow is missing here, Sync everything again fetches every recording you can see; nothing is duplicated.</p> : null}
    </div>
  );
}

// --- Connect form ------------------------------------------------------------------------------

interface FormErrors {
  subdomain?: string;
  apiKey?: string;
  summary: string[];
}

function ConnectForm({ onConnected, previous, title = 'Connect Fellow', onCancel }: {
  onConnected: (response: FellowConnectionResponse) => void;
  previous: FellowConnectionResponse['connection'];
  title?: string;
  /** Present when the form was opened from the connected screen. */
  onCancel?: () => void;
}) {
  // Pre-filled with Centro's workspace: for almost everyone the only thing to type is the key.
  const [subdomain, setSubdomain] = useState(previous?.workspaceSubdomain ?? DEFAULT_FELLOW_WORKSPACE);
  const [apiKey, setApiKey] = useState('');
  const [errors, setErrors] = useState<FormErrors>({ summary: [] });
  const [submitting, setSubmitting] = useState(false);
  const formRef = useRef<HTMLFormElement | null>(null);

  const validate = (): FormErrors => {
    const next: FormErrors = { summary: [] };
    if (subdomain.trim() === '') next.subdomain = 'Enter your workspace subdomain — the part before .fellow.app.';
    if (apiKey.trim() === '') next.apiKey = 'Paste your Fellow API key.';
    next.summary = [next.subdomain, next.apiKey].filter((message): message is string => Boolean(message));
    return next;
  };

  const applyFailure = (failure: ApiFailure): FormErrors => {
    const fields = new Set(failure.fields ?? []);
    const next: FormErrors = { summary: [] };
    if (fields.has('subdomain')) next.subdomain = failure.message;
    if (fields.has('apiKey')) next.apiKey = failure.message;
    if (fields.size === 0) next.summary = [failure.correlationId ? `${inlineFailureText(failure)} (reference ${failure.correlationId})` : inlineFailureText(failure)];
    return next;
  };

  const focusFirstProblem = (): void => {
    window.setTimeout(() => {
      const form = formRef.current;
      if (!form) return;
      const summary = form.querySelector<HTMLElement>('.form-error-summary');
      const invalid = form.querySelector<HTMLElement>('[aria-invalid="true"]');
      (summary ?? invalid)?.focus();
      invalid?.scrollIntoView({ block: 'center' });
    }, 0);
  };

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    const checked = validate();
    if (checked.summary.length > 0) {
      setErrors(checked);
      focusFirstProblem();
      return;
    }
    setSubmitting(true);
    const body: PutFellowConnectionRequest = { subdomain: subdomain.trim(), apiKey: apiKey.trim() };
    const result = await api.put<FellowConnectionResponse>('/api/connections/fellow', body);
    setSubmitting(false);
    if (!result.ok) {
      setErrors(applyFailure(result));
      focusFirstProblem();
      return;
    }
    setApiKey('');
    onConnected(result.data);
  };

  return (
    <section className="stack stack--3" id="connect-fellow" aria-labelledby="connect-fellow-heading">
      <h3 id="connect-fellow-heading">{title}</h3>
      <form ref={formRef} className="connect-form sheet stack" onSubmit={(event) => void submit(event)} noValidate autoComplete="off">
        <FormErrorSummary errors={errors.summary} />
        {previous && previous.status === 'revoked' ? (
          <p className="text-sm secondary">You disconnected {previous.workspaceName ?? previous.workspaceSubdomain} earlier; your imported meetings are kept.</p>
        ) : null}
        {onCancel ? <p className="text-sm secondary">A new key replaces the stored one; your imported meetings are kept.</p> : null}
        <TextField
          label="Workspace subdomain"
          required
          value={subdomain}
          onChange={(event) => setSubdomain(event.target.value)}
          onBlur={() => { if (subdomain.trim() !== '' && errors.subdomain) setErrors((current) => ({ ...current, subdomain: undefined })); }}
          error={errors.subdomain}
          hint="The part before .fellow.app in your Fellow address."
          placeholder={DEFAULT_FELLOW_WORKSPACE}
          autoComplete="off"
          autoCapitalize="none"
          spellCheck={false}
          inputMode="url"
        />
        <TextField
          label="API key"
          required
          type="password"
          value={apiKey}
          onChange={(event) => setApiKey(event.target.value)}
          error={errors.apiKey}
          hint="Your personal key from Fellow’s Developer API settings (shown once when generated). Calis stores it encrypted and never shows it again."
          autoComplete="off"
          spellCheck={false}
        />
        <div className="cluster">
          <Button type="submit" variant="primary" icon="plug" loading={submitting}>Connect</Button>
          {onCancel ? <Button variant="secondary" onClick={onCancel} disabled={submitting}>Cancel</Button> : null}
        </div>
      </form>
    </section>
  );
}
